import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Lock, 
  Loader2,
  FileText,
  Sparkles,
  CreditCard,
  RefreshCw,
  Shield,
  Edit3,
  ArrowLeft
} from "lucide-react";
import axios from "axios";
import Letterhead from "@/components/Letterhead";
import SpeechText from "@/components/SpeechText";
import { trackCheckoutStarted } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreviewPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenMessageIndex, setRegenMessageIndex] = useState(0);
  const [contents, setContents] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [tones, setTones] = useState([]);
  const [selectedTone, setSelectedTone] = useState("");
  const [activeDraft, setActiveDraft] = useState(1);
  const [draft2Loading, setDraft2Loading] = useState(false);
  const [draft2Ready, setDraft2Ready] = useState(false);

  const contentIds = useMemo(() => 
    searchParams.get("content_ids")?.split(",").filter(Boolean) || [],
    [searchParams]
  );

  const fetchContents = useCallback(async () => {
    if (contentIds.length === 0) {
      navigate("/");
      return;
    }

    setLoading(true);
    try {
      const [contentsRes, tonesRes] = await Promise.all([
        Promise.all(contentIds.map(id => axios.get(`${API}/content/${id}`))),
        axios.get(`${API}/tones`)
      ]);
      
      const fetchedContents = contentsRes.map(r => r.data);
      setContents(fetchedContents);
      setTones(tonesRes.data);

      // Calculate price
      setTotalPrice(fetchedContents.reduce((sum, c) => sum + c.price_dkk, 0));
    } catch (error) {
      console.error("Error fetching content:", error);
      toast.error("Kunne ikke hente indhold. Prøv igen.");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [contentIds, navigate]);

  useEffect(() => {
    fetchContents();
    window.scrollTo(0, 0);
  }, [fetchContents]);

  // Auto-generate draft 2 in background
  useEffect(() => {
    if (contents.length === 0) return;
    const content = contents[0];
    if (content.preview_content_draft2) {
      setDraft2Ready(true);
      return;
    }
    
    const generateDraft2 = async () => {
      setDraft2Loading(true);
      try {
        const res = await axios.post(`${API}/speeches/${content.id}/generate-draft2`);
        setContents(prev => prev.map(c => 
          c.id === content.id 
            ? { ...c, preview_content_draft2: res.data.preview_content_draft2 }
            : c
        ));
        setDraft2Ready(true);
      } catch (err) {
        console.error("Draft 2 generation failed:", err);
      } finally {
        setDraft2Loading(false);
      }
    };
    generateDraft2();
  }, [contents.length > 0 && contents[0]?.id]);

  const regenMessages = [
    "Finder en ny vinkel på din tale...",
    "Justerer tonen til den nye stemning...",
    "Væver dine anekdoter ind på en ny måde...",
    "Polerer de sidste formuleringer...",
    "Næsten klar med din nye version!"
  ];

  useEffect(() => {
    if (!regenerating) return;
    setRegenMessageIndex(0);
    const interval = setInterval(() => {
      setRegenMessageIndex(prev => prev < regenMessages.length - 1 ? prev + 1 : prev);
    }, 5000);
    return () => clearInterval(interval);
  }, [regenerating, regenMessages.length]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        content_ids: contentIds,
        origin_url: window.location.origin
      });

      // Track GA4 begin_checkout event
      const firstContent = contents?.[0];
      trackCheckoutStarted(
        response.data.total_price_dkk,
        firstContent?.length_type || "unknown"
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Kunne ikke starte betaling. Prøv igen.");
      setCheckoutLoading(false);
    }
  };

  const handleTryDifferentTone = async () => {
    if (!selectedTone) {
      toast.error("Vælg venligst en tone");
      return;
    }

    // Check if current content already has this tone
    const currentContent = contents[0];
    if (!currentContent) return;

    setRegenerating(true);
    toast.info("Genererer med ny tone...");
    
    try {
      // Get the original form data and regenerate with new tone
      const contentRes = await axios.get(`${API}/content/${currentContent.id}/full`);
      const originalFormData = contentRes.data.form_data;
      
      if (!originalFormData) {
        throw new Error("Kunne ikke finde original data");
      }
      
      // Update tone in form data
      const updatedFormData = { ...originalFormData, tone: selectedTone };
      
      // Regenerate speech
      const response = await axios.post(`${API}/speeches/generate`, updatedFormData);
      
      // Navigate to new preview
      navigate(`/preview?content_ids=${response.data.id}`);
      toast.success("Din nye version er klar!");
      
      // Refresh page to show new content
      window.location.reload();
    } catch (error) {
      console.error("Error regenerating:", error);
      toast.error("Kunne ikke generere ny version. Prøv igen.");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading || regenerating) {
    const messages = regenerating ? regenMessages : ["Indlæser din forhåndsvisning..."];
    const msgIndex = regenerating ? regenMessageIndex : 0;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="preview-loading">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full border-4 border-muted" />
            <div className="absolute inset-0 rounded-full border-4 border-t-secondary animate-spin" />
            <Sparkles className="absolute inset-0 m-auto h-8 w-8 text-secondary" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-primary mb-3">
            {regenerating ? "Skriver din tale om..." : "Indlæser forhåndsvisning..."}
          </h2>
          <p className="text-muted-foreground mb-6 text-lg transition-all duration-500">
            {messages[msgIndex]}
          </p>
          {regenerating && (
            <div className="bg-muted/50 rounded-full px-4 py-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4" />
              Ca. 30 sek. — vi prøver en ny tilgang
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Back to edit */}
        <button
          onClick={() => navigate("/tale?resume=1")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
          data-testid="preview-back-to-edit"
        >
          <ArrowLeft className="h-4 w-4" />
          Tilbage og rediger
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Din indhold er klar!
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary">
            Forhåndsvisning
          </h1>
          <p className="text-muted-foreground mt-2">
            Se et uddrag af dit personlige indhold nedenfor
          </p>
        </div>

        {/* Content Previews with Letterhead */}
        <div className="space-y-6">
          {contents.map((content) => {
            const hasDraft2 = !!content.preview_content_draft2;
            const previewText = activeDraft === 2 && hasDraft2
              ? content.preview_content_draft2
              : content.preview_content;
            
            return (
              <div key={content.id} data-testid={`preview-card-${content.content_type}`}>
                {/* Draft tabs */}
                <div className="flex gap-2 mb-4 items-center" data-testid="draft-tabs">
                  <button
                    onClick={() => setActiveDraft(1)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeDraft === 1
                        ? "bg-primary text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    data-testid="draft-tab-1"
                  >
                    Udkast 1
                  </button>
                  <button
                    onClick={() => draft2Ready && setActiveDraft(2)}
                    disabled={!draft2Ready}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeDraft === 2
                        ? "bg-primary text-white"
                        : draft2Ready 
                          ? "bg-muted text-muted-foreground hover:bg-muted/80"
                          : "bg-muted/50 text-muted-foreground/50 cursor-wait"
                    }`}
                    data-testid="draft-tab-2"
                  >
                    {draft2Loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Udkast 2 skrives...
                      </span>
                    ) : (
                      "Udkast 2"
                    )}
                  </button>
                  {draft2Ready && (
                    <span className="text-xs text-muted-foreground ml-2">
                      Vælg dit favoritudkast — du kan mixe og redigere efter køb
                    </span>
                  )}
                </div>

                <Letterhead
                  title={content.speech_title || "Festtale"}
                  subtitle={`Til ${content.recipient_name}`}
                  type={content.content_type}
                  className="relative"
                >
                  {/* Preview content - visible part */}
                  <div className="relative">
                    <div className="font-body text-foreground leading-relaxed">
                      <SpeechText text={previewText} />
                    </div>
                    
                    {/* Blur overlay for locked content */}
                    <div className="mt-4 relative">
                      <div className="text-foreground leading-relaxed whitespace-pre-line text-blur select-none">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
                        Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 
                        Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
                        Duis aute irure dolor in reprehenderit in voluptate velit esse.
                        Cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat.
                      </div>
                      
                      {/* Lock overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FFFEF9]/80 to-[#FFFEF9] flex items-end justify-center pb-4">
                        <div className="flex items-center gap-2 text-muted-foreground bg-muted px-4 py-2 rounded-full">
                          <Lock className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Resten låses op efter betaling
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Letterhead>
              </div>
            );
          })}
        </div>

        {/* Try different tone section */}
        <Card className="mt-6 border-border/40" data-testid="try-tone-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-primary flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-secondary" />
                  Prøv en anden tone?
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Ikke helt den rigtige stemning? Prøv gratis med en anden tone.
                </p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Select value={selectedTone} onValueChange={setSelectedTone}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="tone-selector">
                    <SelectValue placeholder="Vælg tone" />
                  </SelectTrigger>
                  <SelectContent>
                    {tones.map((tone) => (
                      <SelectItem key={tone.id} value={tone.id}>
                        {tone.name_da}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={handleTryDifferentTone}
                  disabled={regenerating || !selectedTone}
                  data-testid="try-tone-btn"
                >
                  {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Prøv"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Checkout section */}
        <Card className="mt-8 border-t-4 border-t-secondary" data-testid="checkout-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl font-bold text-primary">
                  Din festtale
                </h3>
                <div className="text-muted-foreground mt-1">
                  Personlig tale til {contents[0]?.recipient_name}
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-4xl font-bold text-primary">
                  {totalPrice} kr
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Inkl. redigering & PDF
                </p>
              </div>
            </div>

            {/* Trust elements */}
            <div className="flex flex-wrap gap-4 mt-6 mb-6 justify-center text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Shield className="h-4 w-4 text-accent" />
                100% tilfredshedsgaranti
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Edit3 className="h-4 w-4 text-accent" />
                Tilpas selv teksten
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <RefreshCw className="h-4 w-4 text-accent" />
                Gratis ny version i 24 timer
              </span>
            </div>

            <Button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full py-6 text-lg"
              data-testid="checkout-btn"
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Forbereder betaling...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-5 w-5" />
                  Lås op og betal {totalPrice} kr
                </>
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Sikker betaling via Stripe. Du kan tilpasse teksten efter køb.
            </p>
          </CardContent>
        </Card>

        {/* Try again option */}
        <div className="text-center mt-6">
          <p className="text-muted-foreground text-sm">
            Vil du ændre dine input?{" "}
            <Link 
              to="/tale"
              className="text-primary hover:underline font-medium"
              data-testid="try-again-link"
            >
              Prøv igen med andre detaljer (gratis)
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreviewPage;
